import { useEffect } from "react";

const USABLE_MARK = "app-usable-content";
let usableContentMarked = false;
let navigationSequence = 0;
let lastUsableContentMark: {
  route?: string;
  reason?: string;
  navigationId?: number | null;
} | null = null;

type RoutePreloader = () => Promise<unknown>;
type PreloadState = "idle" | "loading" | "ready" | "failed";

interface RoutePreloadRecord {
  loader: RoutePreloader;
  state: PreloadState;
  promise?: Promise<boolean>;
}

export interface RouteNavigationOptions {
  /** The route was already represented by an open app tab. */
  cacheReuse?: boolean;
  /** Why the navigation was initiated (used only for RUM/debugging). */
  source?: "link" | "tab-revisit" | "query-cache" | "direct" | "unknown";
}

export interface RouteReadyOptions {
  /**
   * Whether useful rows came from an existing query cache. "stale" is
   * intentional: stale cached rows are still usable while their refresh runs.
   */
  cacheState?: "hit" | "stale" | "miss" | "unknown";
  /** A background refresh is still in flight after useful content rendered. */
  refreshPending?: boolean;
}

interface ActiveNavigation {
  id: number;
  route: string;
  preloaded: boolean;
  cacheReuse: boolean;
  source: RouteNavigationOptions["source"];
}

const routePreloaders = new Map<string, RoutePreloadRecord>();
let activeNavigation: ActiveNavigation | null = null;

function routePreloadKey(route: string): string {
  return route.split(/[?#]/, 1)[0] || "/";
}

/** Customer identifiers must never become a RUM dimension or log field. */
function privacySafeNavigationRoute(route: string): string {
  return route.replace(
    /^(\/customers\/)[^/?#]+(?=\/|$)/,
    "$1:id",
  );
}

function dispatch(name: string, detail: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // DOM events are optional in non-browser/test runtimes.
  }
}

function dispatchUsableContent(detail: Record<string, unknown>): void {
  lastUsableContentMark = {
    route: typeof detail.route === "string" ? detail.route : undefined,
    reason: typeof detail.reason === "string" ? detail.reason : undefined,
    navigationId: typeof detail.navigationId === "number" ? detail.navigationId : null,
  };
  dispatch("app:usable-content", detail);
}

/** Read the latest route-ready event for shell work that mounts after it. */
export function getLastUsableContentMark() {
  return lastUsableContentMark;
}

/** Register a lazy route's module loader without importing it eagerly. */
export function registerRoutePreload(route: string, loader: RoutePreloader): void {
  routePreloaders.set(routePreloadKey(route), { loader, state: "idle" });
}

/**
 * Fetch a route chunk once. Calling this from pointer/focus intent or an idle
 * callback is safe: dynamic imports are shared by the bundler and React.lazy
 * will reuse the resolved module rather than issue a second request.
 */
export function preloadRoute(route: string): Promise<boolean> {
  const key = routePreloadKey(route);
  const record = routePreloaders.get(key);
  if (!record) return Promise.resolve(false);
  if (record.promise) return record.promise;

  record.state = "loading";
  dispatch("app:route-preload", { route: key, state: record.state });
  record.promise = record.loader()
    .then(() => {
      record.state = "ready";
      dispatch("app:route-preload", { route: key, state: record.state });
      return true;
    })
    .catch((error: unknown) => {
      record.state = "failed";
      dispatch("app:route-preload", {
        route: key,
        state: record.state,
        error: error instanceof Error ? error.name : "unknown",
      });
      // A failed network import must remain retryable after connectivity
      // returns; do not leave a rejected promise in the registry.
      record.promise = undefined;
      return false;
    });
  return record.promise;
}

export function getRoutePreloadState(route: string): PreloadState {
  return routePreloaders.get(routePreloadKey(route))?.state ?? "idle";
}

/** Kept as an explicit boot hook; navigation timing itself starts at t=0. */
export function seedNavigationStart(): void {
  // Do not create a mark here: main.tsx runs after the module graph has
  // evaluated. PerformanceNavigationTiming.startTime (0) is the true cold
  // navigation origin and includes that work.
}

/** Start a fresh measurement for each warm or cold route navigation. */
export function beginRouteNavigation(
  route: string,
  options: RouteNavigationOptions = {},
): void {
  usableContentMarked = false;
  const id = ++navigationSequence;
  const safeRoute = privacySafeNavigationRoute(route);
  lastUsableContentMark = null;
  // Keep the stable mark name for existing RUM consumers; repeated marks are
  // ordered by the Performance Timeline and the latest one owns this route.
  const startMark = `app-route-start:${safeRoute}`;
  activeNavigation = {
    id,
    route: safeRoute,
    preloaded: getRoutePreloadState(route) === "ready",
    cacheReuse: options.cacheReuse === true,
    source: options.source ?? "unknown",
  };
  dispatch("app:route-navigation", {
    route: safeRoute,
    navigationId: id,
    preloaded: activeNavigation.preloaded,
    cacheReuse: activeNavigation.cacheReuse,
    source: activeNavigation.source,
  });
  if (typeof performance === "undefined") return;
  try {
    performance.mark(startMark);
  } catch {
    // Performance APIs are optional.
  }
}

/**
 * Annotate the current navigation after TabsProvider resolves whether the URL
 * belongs to an existing tab. This deliberately does not claim an HTTP cache
 * hit: it records that a remounted route can reuse its QueryClient entry.
 */
export function reportRouteCacheReuse(
  route: string,
  reused: boolean,
  source: "tab-revisit" | "query-cache" = "tab-revisit",
): void {
  const safeRoute = privacySafeNavigationRoute(route);
  if (activeNavigation?.route === safeRoute) {
    activeNavigation.cacheReuse = reused;
    activeNavigation.source = source;
  }
  dispatch("app:route-cache", {
    route: safeRoute,
    navigationId: activeNavigation?.route === safeRoute ? activeNavigation.id : null,
    reused,
    source,
  });
}

/** Mark readiness after the caller has rendered its useful content. */
export function markAppUsableContent(
  reason: string,
  options: RouteReadyOptions = {},
): void {
  const route = activeNavigation?.route
    ?? (typeof window !== "undefined" ? window.location.pathname : undefined);
  const navigation = activeNavigation;
  if (typeof performance === "undefined") {
    dispatchUsableContent({
      duration: 0,
      reason,
      route,
      navigationId: navigation?.id ?? null,
      preloaded: navigation?.preloaded ?? false,
      cacheReuse: navigation?.cacheReuse ?? false,
      source: navigation?.source ?? "unknown",
      cacheState: options.cacheState ?? "unknown",
      refreshPending: options.refreshPending ?? false,
    });
    return;
  }
  try {
    const routeStarts = performance
      .getEntriesByType("mark")
      .filter((entry) => entry.name.startsWith("app-route-start:"));
    const routeStart = routeStarts[routeStarts.length - 1];
    // The first useful surface owns the User Timing measure, but later
    // route-specific surfaces must still emit their readiness event. Otherwise
    // an unrelated background component can consume the single global mark and
    // make a navigation gate wait forever for the target route's reason.
    if (usableContentMarked) {
      dispatchUsableContent({
        duration: Math.max(0, performance.now() - (routeStart?.startTime ?? 0)),
        reason,
        route,
        navigationId: navigation?.id ?? null,
        preloaded: navigation?.preloaded ?? false,
        cacheReuse: navigation?.cacheReuse ?? false,
        source: navigation?.source ?? "unknown",
        cacheState: options.cacheState ?? "unknown",
        refreshPending: options.refreshPending ?? false,
      });
      return;
    }
    usableContentMarked = true;
    performance.mark(USABLE_MARK);
    const start = routeStart?.name;
    if (start) {
      performance.measure("app-usable-content", start, USABLE_MARK);
    } else {
      // User Timing Level 3 supports numeric start values. Zero is navigation
      // start, unlike a mark created after module evaluation.
      performance.measure("app-usable-content", { start: 0, end: performance.now() });
    }
    const measures = performance.getEntriesByName("app-usable-content", "measure");
    dispatchUsableContent({
      duration: measures[measures.length - 1]?.duration ?? 0,
      reason,
      route,
      navigationId: navigation?.id ?? null,
      preloaded: navigation?.preloaded ?? false,
      cacheReuse: navigation?.cacheReuse ?? false,
      source: navigation?.source ?? "unknown",
      cacheState: options.cacheState ?? "unknown",
      refreshPending: options.refreshPending ?? false,
    });
  } catch {
    usableContentMarked = false;
  }
}

/** Shared readiness hook for route shells and data-owning primary views. */
export function markRouteReady(reason: string, options?: RouteReadyOptions): void {
  markAppUsableContent(reason, options);
}

/**
 * Measure the time from a client mutation start to the first usable cache
 * state. Callers mark the start before sending and call this immediately after
 * applying the authoritative mutation payload; it intentionally does not wait
 * for broad query invalidation.
 */
export function markMutationUsable(mutation: string): void {
  if (typeof performance === "undefined") return;
  const safeName = mutation.replace(/[^a-zA-Z0-9_-]/g, "-");
  const startName = `client-mutation-start:${safeName}`;
  const usableName = `client-mutation-usable:${safeName}`;
  try {
    performance.mark(usableName);
    const starts = performance
      .getEntriesByName(startName, "mark")
      .filter((entry) => entry.startTime <= performance.now());
    const start = starts[starts.length - 1];
    const duration = Math.max(
      0,
      performance.now() - (start?.startTime ?? performance.now()),
    );
    if (start) {
      performance.measure(
        `client-mutation-to-usable:${safeName}`,
        startName,
        usableName,
      );
    }
    dispatch("app:mutation-usable", { mutation: safeName, duration });
  } catch {
    // Performance APIs are optional and must not affect mutation success.
  }
}

/**
 * Small, privacy-safe browser timing hook. It deliberately only creates
 * PerformanceEntry marks/measures and a DOM event; an analytics transport can
 * consume these later without making app readiness depend on the network.
 */
export function useAppPerformanceInstrumentation(fallbackDelayMs = 3000): void {
  useEffect(() => {
    if (typeof performance === "undefined") return;

    try {
      // Authenticated shells/pages call markAppUsableContent when useful data
      // is rendered. The delayed fallback is for public/error surfaces that
      // have no signed-in readiness query to observe.
      const timer = window.setTimeout(() => markAppUsableContent("public-surface"), fallbackDelayMs);
      return () => {
        window.clearTimeout(timer);
      };
    } catch {
      // Performance APIs are optional (older webviews and restricted browsers).
      return undefined;
    }
  }, []);
}

/** Read-only navigation timing snapshot for RUM/reporting integrations. */
export function getBrowserTiming(): {
  responseStart?: number;
  domInteractive?: number;
  domContentLoaded?: number;
  loadEventEnd?: number;
} | null {
  if (typeof performance === "undefined") return null;
  const entry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (!entry) return null;
  return {
    responseStart: entry.responseStart,
    domInteractive: entry.domInteractive,
    domContentLoaded: entry.domContentLoadedEventEnd,
    loadEventEnd: entry.loadEventEnd,
  };
}